import React, { useState } from "react";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Stack,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider,
    Alert,
    CircularProgress,
} from "@mui/material";

import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import api from "../../../services/api";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../../../utils/analytics/gtag";

const InstacartCheckoutButton = ({
    selectedGenericIds = [],
    items = [],
    context = "saved_list",
    recipeId = null,
    servings = null,
    lang = null,
    recipeSlug = null,
}) => {
    const { t, i18n } = useTranslation();

    const [open, setOpen] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [error, setError] = useState(null);
    const [confirming, setConfirming] = useState(false);

    const hasSelection = selectedGenericIds && selectedGenericIds.length > 0;

    const INSTACART_ENABLED =
        String(process.env.REACT_APP_INSTACART_ENABLED || "false") === "true";

    const comingSoonLabel = t("instacart.comingSoon", "Checkout (Coming Soon)");
    const comingSoonBody = t(
        "instacart.comingSoonBody",
        "We’re preparing a seamless Instacart auto-checkout experience. Preview is available — checkout will be enabled soon."
    );

    // ✅ ΔΕΝ στέλνουμε zip_code εδώ. Το παίρνει ο backend από _resolve_geo_from_profile_or_guest().
    const buildPayload = () => {
        const base = { lang: lang || i18n.language || "en" };

        if (context === "recipe") {
            return {
                ...base,
                mode: "recipe",
                recipe_id: recipeId,
                servings: servings || 4,
                include_generic_ids: selectedGenericIds,
            };
        }

        if (context === "custom_list") {
            return {
                ...base,
                mode: "custom",
                items: items,
                include_generic_ids: selectedGenericIds,
            };
        }

        return {
            ...base,
            mode: "saved_list",
            include_generic_ids: selectedGenericIds,
        };
    };

    const normalizeBackendError = (err, fallbackKey, fallbackText) => {
        const status = err?.response?.status;
        const backendErr = err?.response?.data?.error;

        if (status === 401 || status === 403 || backendErr === "AUTH_REQUIRED") {
            return (
                t(
                    "instacartAuthRequiredError",
                    "This action is currently blocked for guests. Please enable guest checkout on the backend."
                ) || fallbackText
            );
        }

        return backendErr || t(fallbackKey, fallbackText);
    };

    const handleOpen = async () => {
        if (!hasSelection) return;

        setOpen(true);
        setError(null);
        setPreviewData(null);
        setLoadingPreview(true);

        // ✅ Analytics: separate event for coming soon
        trackEvent(INSTACART_ENABLED ? "instacart_preview_open" : "instacart_coming_soon_open", {
            context,
            recipe_id: recipeId || undefined,
            recipe_slug: recipeSlug || undefined,
            lang: lang || i18n.language || "en",
            selected_count: Array.isArray(selectedGenericIds) ? selectedGenericIds.length : 0,
            source_path: window.location.pathname,
        });

        // ✅ IMPORTANT: Even when disabled, we still load preview so users can see items.
        try {
            const payload = buildPayload();
            const res = await api.shoppingList.instacartPreview(payload);

            if (res.data && res.data.success) {
                setPreviewData(res.data);
            } else {
                setError(
                    res.data?.error ||
                    t("instacartPreviewError", "Failed to load Instacart preview.")
                );
            }
        } catch (err) {
            console.error("[Instacart PREVIEW] error:", err);
            setError(
                normalizeBackendError(
                    err,
                    "instacartPreviewError",
                    "Failed to load Instacart preview."
                )
            );
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleClose = () => {
        if (confirming) return;
        setOpen(false);
        setPreviewData(null);
        setError(null);
    };

    const handleConfirm = async () => {
        // ✅ Hard safety lock: never allow checkout when disabled
        if (!INSTACART_ENABLED) {
            setError(t("instacartCheckoutDisabled", "Checkout is coming soon."));
            return;
        }

        if (!previewData || !hasSelection) return;

        setConfirming(true);
        setError(null);

        try {
            const currentUrl =
                window.location.pathname + window.location.search + window.location.hash;

            const payload = { ...buildPayload(), return_path: currentUrl };
            const res = await api.shoppingList.checkoutInstacart(payload);

            const redirectUrl = res?.data?.redirect_url || res?.data?.url;
            if (res?.data?.success && redirectUrl) {
                localStorage.setItem("post_instacart_redirect", currentUrl);
                localStorage.setItem("post_instacart_redirect_ts", String(Date.now()));

                trackEvent("instacart_checkout_click", {
                    context,
                    recipe_id: recipeId || undefined,
                    recipe_slug: recipeSlug || undefined,
                    lang: lang || i18n.language || "en",
                    selected_count: Array.isArray(selectedGenericIds) ? selectedGenericIds.length : 0,
                    total_items: previewData?.total_items ?? undefined,
                    ready_count: previewData?.ready_count ?? undefined,
                    missing_count: previewData?.missing_count ?? undefined,
                    source_path: window.location.pathname,
                });

                window.location.href = redirectUrl;
                return;
            }

            setError(
                res?.data?.error ||
                t("instacartCheckoutError", "Failed to generate Instacart URL.")
            );
        } catch (err) {
            console.error("[Instacart CHECKOUT] error:", err);
            setError(
                normalizeBackendError(
                    err,
                    "instacartCheckoutError",
                    "Failed to generate Instacart URL."
                )
            );
        } finally {
            setConfirming(false);
        }
    };

    const totalItems = previewData?.total_items || 0;
    const readyCount = previewData?.ready_count || 0;
    const missingCount = previewData?.missing_count || 0;

    return (
        <>
            <Button
                variant="contained"
                startIcon={<ShoppingCartCheckoutIcon />}
                onClick={handleOpen}
                disabled={!hasSelection}
                sx={{ borderRadius: 999, fontWeight: 600 }}
            >
                {INSTACART_ENABLED
                    ? t("instacart.checkout", "Order via Instacart")
                    : comingSoonLabel}
            </Button>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>
                    {INSTACART_ENABLED
                        ? t("instacartModalTitle", "Instacart Checkout Preview")
                        : t("instacartModalTitleComingSoon", "Instacart Checkout – Coming Soon")}
                </DialogTitle>

                <DialogContent dividers>
                    {!INSTACART_ENABLED && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography sx={{ fontWeight: 700 }}>
                                {t("instacartComingSoonTitle", "Instacart checkout is coming soon")}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {comingSoonBody}
                            </Typography>
                        </Alert>
                    )}

                    {loadingPreview && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            spacing={2}
                            sx={{ py: 4 }}
                        >
                            <CircularProgress />
                            <Typography>
                                {t("instacartLoadingPreview", "Loading your Instacart cart preview...")}
                            </Typography>
                        </Stack>
                    )}

                    {!loadingPreview && error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!loadingPreview && !error && previewData && (
                        <Stack spacing={3}>
                            <BoxHeader
                                t={t}
                                readyCount={readyCount}
                                totalItems={totalItems}
                                missingCount={missingCount}
                            />

                            <Divider />

                            <BoxReadyItems t={t} previewData={previewData} />
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} disabled={confirming}>
                        {t("close", "Close")}
                    </Button>

                    {INSTACART_ENABLED && (
                        <Button
                            variant="contained"
                            startIcon={<ShoppingCartCheckoutIcon />}
                            onClick={handleConfirm}
                            disabled={
                                confirming ||
                                !previewData ||
                                !previewData.ready_items ||
                                previewData.ready_items.length === 0
                            }
                        >
                            {confirming
                                ? t("instacartConfirming", "Redirecting to Instacart...")
                                : t("instacartContinue", "Continue with Instacart")}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

// Helpers
const BoxHeader = ({ t, readyCount, totalItems, missingCount }) => (
    <div>
        <Typography variant="subtitle1" gutterBottom>
            {t("instacartSummaryTitle", "Summary of your Instacart order")}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
                icon={<CheckCircleOutlineIcon />}
                label={t("instacartReadyItems", "{{ready}} of {{total}} items available on Instacart", {
                    ready: readyCount,
                    total: totalItems,
                })}
                color="success"
                variant="outlined"
            />
            {missingCount > 0 && (
                <Chip
                    icon={<WarningAmberIcon />}
                    label={t("instacartMissingItems", "{{missing}} items not available", {
                        missing: missingCount,
                    })}
                    color="warning"
                    variant="outlined"
                />
            )}
        </Stack>

        {missingCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningAmberIcon />}>
                {t(
                    "instacartMissingNote",
                    "Items not available on Instacart will remain in your list so you can buy them elsewhere."
                )}
            </Alert>
        )}
    </div>
);

const BoxReadyItems = ({ t, previewData }) => (
    <div>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography variant="subtitle1">
                {t("instacartReadyListTitle", "Will be added to Instacart")}
            </Typography>
        </Stack>

        {previewData.ready_items && previewData.ready_items.length > 0 ? (
            <List dense>
                {previewData.ready_items.map((item) => {
                    const displayProductName =
                        item.us_product_name && item.us_product_name !== item.generic_name
                            ? item.us_product_name
                            : null;

                    const displayStore =
                        item.store_name && item.store_chain
                            ? item.store_name === item.store_chain
                                ? item.store_name
                                : `${item.store_name} (${item.store_chain})`
                            : item.store_name || item.store_chain || null;

                    return (
                        <ListItem
                            key={`ready-${item.generic_product_id}`}
                            disableGutters
                            sx={{ mb: 1 }}
                        >
                            <ListItemText
                                primary={
                                    <>
                                        <Typography variant="subtitle2">
                                            {item.generic_name || item.name}
                                        </Typography>

                                        {displayProductName && (
                                            <Typography variant="body2" color="text.secondary">
                                                {displayProductName}
                                                {item.package_size_text ? ` — ${item.package_size_text}` : ""}
                                            </Typography>
                                        )}
                                    </>
                                }
                                secondary={
                                    <Stack spacing={0.5}>
                                        <Typography variant="body2">
                                            {t("instacartItemQtyDetailed", "Needed: {{qty}} g (≈ {{qty_us}} {{unit_us}})", {
                                                qty: item.qty_g ?? item.qty,
                                                qty_us: item.qty_us,
                                                unit_us: item.unit_us,
                                            })}
                                        </Typography>

                                        {displayStore && (
                                            <Typography variant="body2" color="text.secondary">
                                                {t("instacartStore", "Store: {{store}}", { store: displayStore })}
                                            </Typography>
                                        )}
                                    </Stack>
                                }
                            />
                        </ListItem>
                    );
                })}
            </List>
        ) : (
            <Typography variant="body2" color="text.secondary">
                {t("instacartNoReadyItems", "No items are available on Instacart from your current selection.")}
            </Typography>
        )}
    </div>
);

export default InstacartCheckoutButton;
